import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import { fetchJobErrors } from './services/lms-provisioning.service';
import {
  canRetryJob,
  canViewJobErrors,
  jobProgressPercent,
  normalizeJobErrors,
  statusTier,
  summarizeJob,
} from './utils/provisioning-jobs.utils';

const formatTimestamp = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const JobErrorsPanel = ({ jobId, expanded }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [fetchError, setFetchError] = useState(null);

  React.useEffect(() => {
    if (!expanded) return undefined;

    let cancelled = false;
    setLoading(true);
    setFetchError(null);
    setErrors([]);

    fetchJobErrors(jobId)
      .then((payload) => {
        if (cancelled) return;
        setErrors(normalizeJobErrors(payload));
      })
      .catch((err) => {
        if (cancelled) return;
        setFetchError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [expanded, jobId]);

  if (!expanded) return null;

  return (
    <Box sx={{ py: 1.5, px: 1 }} data-testid={`job-errors-panel-${jobId}`}>
      {loading && <LinearProgress sx={{ mb: 1 }} data-testid={`job-errors-loading-${jobId}`} />}
      {fetchError && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          {fetchError.message || 'Failed to load job errors.'}
        </Typography>
      )}
      {!loading && !fetchError && errors.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No record-level errors reported for this job.
        </Typography>
      )}
      {!loading && errors.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Record</TableCell>
              <TableCell>Error</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {errors.map((row, index) => (
              <TableRow key={`${jobId}-error-${index}`} data-testid={`job-errors-row-${index}`}>
                <TableCell>{row.recordLabel}</TableCell>
                <TableCell>{row.message}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

JobErrorsPanel.propTypes = {
  jobId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  expanded: PropTypes.bool.isRequired,
};

const ProvisioningJobRow = ({ job, submitting, onRetry, variant, columnCount }) => {
  const [expanded, setExpanded] = useState(false);
  const showErrors = canViewJobErrors(job);
  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  return (
    <>
      <TableRow hover data-testid={variant === 'bulk' ? `facility-bulk-job-row-${job.jobId}` : undefined}>
        <TableCell padding="checkbox" sx={{ width: 48 }}>
          {showErrors ? (
            <IconButton
              size="small"
              aria-label={expanded ? 'Hide job errors' : 'Show job errors'}
              onClick={toggleExpanded}
              data-testid={`job-errors-toggle-${job.jobId}`}
            >
              {expanded ? <ExpandLessRoundedIcon fontSize="small" /> : <ExpandMoreRoundedIcon fontSize="small" />}
            </IconButton>
          ) : null}
        </TableCell>
        {variant === 'bulk' ? (
          <TableCell>{job.jobId}</TableCell>
        ) : (
          <TableCell>{job.jobType}</TableCell>
        )}
        <TableCell>
          <Chip size="small" color={statusTier(job.status)} label={job.status} variant="outlined" />
        </TableCell>
        <TableCell sx={{ minWidth: 120 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box flex={1}>
              <LinearProgress variant="determinate" value={jobProgressPercent(job)} />
            </Box>
            <Typography variant="caption">{jobProgressPercent(job)}%</Typography>
          </Box>
        </TableCell>
        <TableCell>{summarizeJob(job)}</TableCell>
        {variant === 'bulk' && <TableCell>{job.initiatedBy || '—'}</TableCell>}
        <TableCell>{formatTimestamp(job.startedAt || job.createdAt)}</TableCell>
        <TableCell align="right">
          {canRetryJob(job) && (
            <Tooltip title={variant === 'bulk' ? 'Re-run location bulk provision' : 'Re-run this job over the current delta'}>
              <span>
                <Button
                  size="small"
                  startIcon={<ReplayRoundedIcon />}
                  disabled={submitting}
                  onClick={() => onRetry?.(job.jobId)}
                  aria-label={`retry-job-${job.jobId}`}
                  data-testid={variant === 'bulk' ? `facility-bulk-job-retry-${job.jobId}` : undefined}
                >
                  Retry
                </Button>
              </span>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
      {showErrors && (
        <TableRow>
          <TableCell colSpan={columnCount} sx={{ py: 0, borderBottom: expanded ? undefined : 0 }}>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <JobErrorsPanel jobId={job.jobId} expanded={expanded} />
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

ProvisioningJobRow.propTypes = {
  job: PropTypes.object.isRequired,
  submitting: PropTypes.bool,
  onRetry: PropTypes.func,
  variant: PropTypes.oneOf(['facility', 'bulk']).isRequired,
  columnCount: PropTypes.number.isRequired,
};

const ProvisioningJobTable = ({ jobs, submitting, onRetry, variant }) => {
  const columnCount = variant === 'bulk' ? 8 : 7;

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox" sx={{ width: 48 }} />
          {variant === 'bulk' ? <TableCell>Job #</TableCell> : <TableCell>Job</TableCell>}
          <TableCell>Status</TableCell>
          <TableCell>Progress</TableCell>
          <TableCell>Result</TableCell>
          {variant === 'bulk' && <TableCell>Initiated by</TableCell>}
          <TableCell>Started</TableCell>
          <TableCell align="right">Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {jobs.map((job) => (
          <ProvisioningJobRow
            key={job.jobId}
            job={job}
            submitting={submitting}
            onRetry={onRetry}
            variant={variant}
            columnCount={columnCount}
          />
        ))}
      </TableBody>
    </Table>
  );
};

ProvisioningJobTable.propTypes = {
  jobs: PropTypes.arrayOf(PropTypes.object).isRequired,
  submitting: PropTypes.bool,
  onRetry: PropTypes.func,
  variant: PropTypes.oneOf(['facility', 'bulk']).isRequired,
};

export default ProvisioningJobTable;
